import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const OSRM_TRIP_URL = 'https://router.project-osrm.org/trip/v1/driving/';
const NOMINATIM_URL = 'https://nominatim.openstreetmap.org/search';

function decodePolyline(str) {
  let index = 0, lat = 0, lng = 0;
  const coords = [];
  while (index < str.length) {
    let b, shift = 0, result = 0;
    do { b = str.charCodeAt(index++) - 63; result |= (b & 0x1f) << shift; shift += 5; } while (b >= 0x20);
    lat += (result & 1) ? ~(result >> 1) : (result >> 1);
    shift = 0; result = 0;
    do { b = str.charCodeAt(index++) - 63; result |= (b & 0x1f) << shift; shift += 5; } while (b >= 0x20);
    lng += (result & 1) ? ~(result >> 1) : (result >> 1);
    coords.push([lat / 1e5, lng / 1e5]);
  }
  return coords;
}

let nextId = 1;

function makeIcon(color, text) {
  return L.divIcon({
    className: '',
    html: `<div style="background:${color};color:#fff;border-radius:50%;width:28px;height:28px;display:flex;align-items:center;justify-content:center;font-weight:bold;font-size:13px;border:2px solid #fff;box-shadow:0 2px 4px rgba(0,0,0,.4);">${text}</div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
  });
}

const gpsIcon = L.divIcon({
  className: '',
  html: `<div style="background:#f59e0b;color:#fff;border-radius:50%;width:30px;height:30px;display:flex;align-items:center;justify-content:center;font-size:16px;border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.5);">📡</div>`,
  iconSize: [30, 30],
  iconAnchor: [15, 15],
});

const RuteadorContent = ({ initialOrigen, initialOrigenCoords, initialDestino, gpsIds, movId, height = '100%' }) => {
  const [waypoints, setWaypoints] = useState([]);
  const [originId, setOriginId] = useState(null);
  const [destinationId, setDestinationId] = useState(null);
  const [route, setRoute] = useState(null);
  const [info, setInfo] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [coordText, setCoordText] = useState('');
  const [dragIdx, setDragIdx] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [gpsPositions, setGpsPositions] = useState([]);
  const initialized = useRef(false);
  const skipFetch = useRef(false);
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const routeLineRef = useRef(null);
  const gpsMarkersRef = useRef([]);

  // Initialize map
  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return;
    mapRef.current = L.map(mapContainerRef.current).setView([19.386, -99.101], 12);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    }).addTo(mapRef.current);
    mapRef.current.on('click', (e) => {
      const wp = { id: nextId++, coords: [e.latlng.lat, e.latlng.lng], label: 'Punto en mapa' };
      setWaypoints((prev) => [...prev, wp]);
    });
    setTimeout(() => mapRef.current.invalidateSize(), 200);
    return () => { if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; } };
  }, []);

  // Preload origin/destination
  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;
    const pts = [];
    if (initialOrigen) pts.push({ id: nextId++, coords: null, label: initialOrigen, role: 'origen' });
    if (initialDestino) pts.push({ id: nextId++, coords: null, label: initialDestino, role: 'destino' });
    if (pts.length > 0) {
      Promise.all(pts.map((p) =>
        fetch(`${NOMINATIM_URL}?q=${encodeURIComponent(p.label)}&format=json&limit=1&countrycodes=mx`)
          .then((r) => r.json())
          .then((data) => data[0] ? { ...p, coords: [parseFloat(data[0].lat), parseFloat(data[0].lon)] } : null)
          .catch(() => null)
      )).then((results) => {
        const valid = results.map((r, i) => {
          if (r && r.coords) return r;
          if (pts[i].role === 'origen' && initialOrigenCoords) return { ...pts[i], coords: initialOrigenCoords };
          return null;
        }).filter((r) => r && r.coords);
        if (valid.length > 0) {
          setWaypoints(valid);
          const o = valid.find((v) => v.role === 'origen');
          const d = valid.find((v) => v.role === 'destino');
          if (o) setOriginId(o.id);
          if (d) setDestinationId(d.id);
        }
      });
    }
  }, [initialOrigen, initialOrigenCoords, initialDestino]);

  // Fetch GPS positions
  const gpsIdsStr = gpsIds ? gpsIds.join(',') : '';
  useEffect(() => {
    if (!gpsIdsStr) return;
    const ids = gpsIdsStr.split(',');
    const token = sessionStorage.getItem('tok');
    const apiUrl = require('../../config/config').api_raiz;
    console.log('Fetching GPS positions for IDs:', ids);
    Promise.all(ids.map((id) =>
      fetch(`${apiUrl}gpsUltReporte/${id}`, { headers: { 'Authorization': 'Bearer ' + token } })
        .then((r) => { if (!r.ok) return null; return r.text().then((t) => t ? JSON.parse(t) : null); })
        .catch(() => null)
    )).then((results) => {
      setGpsPositions(results.filter((r) => {
        if (!r) return false;
        const lat = parseFloat(r.latitud);
        const lng = parseFloat(r.longitud);
        return !isNaN(lat) && !isNaN(lng) && (lat !== 0 || lng !== 0);
      }).map((r) => ({ ...r, latitud: parseFloat(r.latitud), longitud: parseFloat(r.longitud) })));
    });
  }, [gpsIdsStr]);

  // Ordered points
  const orderedPoints = useMemo(() => {
    const origin = waypoints.find((w) => w.id === originId);
    const dest = waypoints.find((w) => w.id === destinationId);
    const mid = waypoints.filter((w) => w.id !== originId && w.id !== destinationId);
    const result = [];
    if (origin) result.push(origin);
    result.push(...mid);
    if (dest) result.push(dest);
    return result;
  }, [waypoints, originId, destinationId]);

  // Fetch route
  const fetchRoute = useCallback((pts, hasOrigin, hasDest) => {
    if (pts.length < 2) { setRoute(null); setInfo(null); return; }
    const coords = pts.map((wp) => `${wp.coords[1]},${wp.coords[0]}`).join(';');
    const useTrip = pts.length > 2;
    const url = useTrip
      ? `https://router.project-osrm.org/trip/v1/driving/${coords}?overview=full&geometries=geojson&source=first&destination=last&roundtrip=false`
      : `https://router.project-osrm.org/route/v1/driving/${coords}?overview=full&geometries=geojson`;

    fetch(url)
      .then((r) => r.json())
      .then((data) => {
        const trip = (data.trips && data.trips[0]) || (data.routes && data.routes[0]);
        if (trip) {
          setRoute(trip.geometry.coordinates.map(([lng, lat]) => [lat, lng]));
          setInfo({ distance: (trip.distance / 1000).toFixed(1), duration: Math.round(trip.duration / 60) });
          if (data.waypoints && useTrip) {
            const order = data.waypoints.map((w) => w.waypoint_index);
            if (!order.every((v, i) => v === i)) {
              skipFetch.current = true;
              setWaypoints(order.map((idx) => pts[idx]));
            }
          }
        }
      })
      .catch(() => { setRoute(null); setInfo(null); });
  }, []);

  useEffect(() => {
    if (skipFetch.current) { skipFetch.current = false; if (loadedFromDb.current) { loadedFromDb.current = false; fetchRoute(orderedPoints, originId !== null, destinationId !== null); } return; }
    fetchRoute(orderedPoints, originId !== null, destinationId !== null);
  }, [orderedPoints, originId, destinationId, fetchRoute]);

  // Update map markers and route
  useEffect(() => {
    if (!mapRef.current) return;
    // Clear old markers
    markersRef.current.forEach((m) => m.remove());
    markersRef.current = [];
    // Add waypoint markers
    orderedPoints.forEach((wp, i) => {
      const role = wp.id === originId ? 'origen' : wp.id === destinationId ? 'destino' : null;
      let icon;
      if (role === 'origen') icon = makeIcon('#16a34a', 'O');
      else if (role === 'destino') icon = makeIcon('#dc2626', 'D');
      else { const midIdx = orderedPoints.filter((w, j) => j < i && w.id !== originId && w.id !== destinationId).length + 1; icon = makeIcon('#2563eb', midIdx); }
      const marker = L.marker(wp.coords, { icon }).addTo(mapRef.current);
      const roleLabel = role === 'origen' ? '🟢 Origen' : role === 'destino' ? '🔴 Destino' : `${i + 1}.`;
      marker.bindTooltip(`<b>${roleLabel}</b> ${wp.label}`, { permanent: true, direction: 'top', offset: [0, -14] });
      markersRef.current.push(marker);
    });
    // Fit bounds
    if (orderedPoints.length > 0) {
      if (orderedPoints.length === 1) mapRef.current.setView(orderedPoints[0].coords, 13);
      else mapRef.current.fitBounds(L.latLngBounds(orderedPoints.map((p) => p.coords)), { padding: [50, 50] });
    }
  }, [orderedPoints, originId, destinationId]);

  // Update route line
  useEffect(() => {
    if (!mapRef.current) return;
    if (routeLineRef.current) { routeLineRef.current.remove(); routeLineRef.current = null; }
    if (route) { routeLineRef.current = L.polyline(route, { color: 'blue', weight: 5, opacity: 0.7 }).addTo(mapRef.current); }
  }, [route]);

  // Update GPS markers
  useEffect(() => {
    if (!mapRef.current) return;
    gpsMarkersRef.current.forEach((m) => m.remove());
    gpsMarkersRef.current = [];
    gpsPositions.forEach((gps) => {
      const marker = L.marker([gps.latitud, gps.longitud], { icon: gpsIcon }).addTo(mapRef.current);
      marker.bindTooltip(`📡 ${gps.nombre}`, { permanent: true, direction: 'top', offset: [0, -15] });
      marker.bindPopup(`<b>📡 GPS: ${gps.nombre}</b><br/>Lat: ${gps.latitud.toFixed(5)}, Lng: ${gps.longitud.toFixed(5)}<br/>Batería: ${gps.bateria}%<br/>Último reporte: ${gps.utc ? new Date(gps.utc).toLocaleString() : 'N/A'}`);
      gpsMarkersRef.current.push(marker);
    });
  }, [gpsPositions]);

  const addPoint = useCallback((wp) => setWaypoints((prev) => [...prev, wp]), []);
  const removePoint = useCallback((id) => { setWaypoints((prev) => prev.filter((w) => w.id !== id)); setOriginId((p) => p === id ? null : p); setDestinationId((p) => p === id ? null : p); }, []);
  const toggleOrigin = useCallback((id) => { setOriginId((p) => p === id ? null : id); setDestinationId((p) => p === id ? null : p); }, []);
  const toggleDestination = useCallback((id) => { setDestinationId((p) => p === id ? null : id); setOriginId((p) => p === id ? null : p); }, []);

  const handleGeocode = (e) => {
    e.preventDefault();
    if (!searchText.trim()) return;
    fetch(`${NOMINATIM_URL}?q=${encodeURIComponent(searchText)}&format=json&limit=5&addressdetails=1&countrycodes=mx`)
      .then((r) => r.json())
      .then((data) => { if (data.length > 0) setSearchResults(data); else { alert('No se encontraron resultados'); setSearchResults([]); } })
      .catch(() => alert('Error en búsqueda'));
  };

  const selectResult = (item) => {
    addPoint({ id: nextId++, coords: [parseFloat(item.lat), parseFloat(item.lon)], label: item.display_name.split(',').slice(0, 3).join(',') });
    setSearchResults([]);
    setSearchText('');
  };

  const handleCoords = (e) => {
    e.preventDefault();
    const parts = coordText.split(',').map((s) => parseFloat(s.trim()));
    if (parts.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
      addPoint({ id: nextId++, coords: parts, label: coordText.trim() });
      setCoordText('');
    } else { alert('Formato: latitud, longitud'); }
  };

  const clearAll = () => { setWaypoints([]); setOriginId(null); setDestinationId(null); setRoute(null); setInfo(null); };
  const reorder = (from, to) => { setWaypoints((prev) => { const n = [...prev]; const [item] = n.splice(from, 1); n.splice(to, 0, item); return n; }); };

  const saveRoute = () => {
    if (!movId || orderedPoints.length < 2 || !info) return;
    const token = sessionStorage.getItem('tok');
    const apiUrl = require('../../config/config').api_raiz;
    const user = JSON.parse(sessionStorage.getItem('usuario'));
    const body = {
      idMovimiento: movId,
      waypoints: JSON.stringify(orderedPoints.map((w) => ({ coords: w.coords, label: w.label, role: w.id === originId ? 'origen' : w.id === destinationId ? 'destino' : null }))),
      distanciaKm: parseFloat(info.distance),
      duracionMin: info.duration,
      idUsuario: user ? user.id : null,
    };
    fetch(`${apiUrl}rutasMovimiento`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token },
      body: JSON.stringify(body),
    }).then((r) => { if (r.ok) alert('Ruta guardada'); else alert('Error al guardar'); })
      .catch(() => alert('Error al guardar'));
  };

  const loadedFromDb = useRef(false);
  useEffect(() => {
    if (!movId) return;
    const token = sessionStorage.getItem('tok');
    const apiUrl = require('../../config/config').api_raiz;
    fetch(`${apiUrl}rutasMovimiento/movimiento/${movId}`, { headers: { 'Authorization': 'Bearer ' + token } })
      .then((r) => r.ok ? r.json() : [])
      .then((data) => {
        if (data.length > 0 && waypoints.length === 0) {
          const saved = JSON.parse(data[0].waypoints);
          const pts = saved.map((s) => ({ ...s, id: nextId++ }));
          loadedFromDb.current = true;
          skipFetch.current = true;
          setWaypoints(pts);
          const o = pts.find((p) => p.role === 'origen');
          const d = pts.find((p) => p.role === 'destino');
          if (o) setOriginId(o.id);
          if (d) setDestinationId(d.id);
        }
      })
      .catch(() => {});
  }, [movId]);

  const panelStyle = { position: 'absolute', top: 10, left: 10, zIndex: 1000, background: 'white', padding: 12, borderRadius: 6, boxShadow: '0 2px 8px rgba(0,0,0,0.3)', width: 300, maxHeight: '80%', overflowY: 'auto' };
  const inputStyle = { width: '100%', padding: '4px 6px', boxSizing: 'border-box' };
  const btnStyle = { padding: '4px 10px', cursor: 'pointer', marginTop: 4 };
  const roleBtnBase = { cursor: 'pointer', border: '1px solid', borderRadius: 3, padding: '1px 5px', fontSize: 10, marginLeft: 3 };

  return (
    <div style={{ width: '100%', height, position: 'relative' }}>
      <div style={panelStyle}>
        <b>Ruteador</b>
        <form onSubmit={handleGeocode} style={{ marginTop: 8 }}>
          <input style={inputStyle} placeholder="Buscar dirección..." value={searchText} onChange={(e) => { setSearchText(e.target.value); setSearchResults([]); }} />
          <button type="submit" style={btnStyle}>Buscar</button>
        </form>
        {searchResults.length > 0 && (
          <ul style={{ listStyle: 'none', padding: 0, margin: '4px 0', fontSize: 12, maxHeight: 120, overflowY: 'auto', border: '1px solid #ddd', borderRadius: 4 }}>
            {searchResults.map((item, i) => (
              <li key={i} onClick={() => selectResult(item)}
                style={{ padding: '5px 8px', cursor: 'pointer', borderBottom: '1px solid #eee', background: i % 2 ? '#f9f9f9' : '#fff' }}
                onMouseEnter={(ev) => ev.target.style.background = '#e0edff'}
                onMouseLeave={(ev) => ev.target.style.background = i % 2 ? '#f9f9f9' : '#fff'}
              >📍 {item.display_name}</li>
            ))}
          </ul>
        )}
        <form onSubmit={handleCoords} style={{ marginTop: 8 }}>
          <input style={inputStyle} placeholder="lat, lng (ej: 19.4326, -99.1332)" value={coordText} onChange={(e) => setCoordText(e.target.value)} />
          <button type="submit" style={btnStyle}>Agregar</button>
        </form>
        <div style={{ marginTop: 6, fontSize: 12, color: '#666' }}>Clic en el mapa para agregar puntos</div>
        {gpsPositions.length > 0 && (
          <div style={{ marginTop: 8, padding: '6px 8px', background: '#fff7e6', borderRadius: 4, fontSize: 12 }}>
            📡 GPS en mapa: {gpsPositions.map((g, i) => (
              <span key={i}>
                {i > 0 && ', '}
                <a href="#" onClick={(e) => { e.preventDefault(); if (mapRef.current) mapRef.current.setView([g.latitud, g.longitud], 15); }}
                  style={{ color: '#d97706', textDecoration: 'underline', cursor: 'pointer' }}>{g.nombre}</a>
              </span>
            ))}
          </div>
        )}
        {gpsIdsStr && gpsPositions.length === 0 && (
          <div style={{ marginTop: 8, padding: '6px 8px', background: '#fee', borderRadius: 4, fontSize: 12, color: '#a00' }}>
            ⚠️ GPS IDs: [{gpsIdsStr}] — sin datos de posición
          </div>
        )}
        {waypoints.length > 0 && (
          <div style={{ marginTop: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <b>Puntos ({waypoints.length})</b>
              <button onClick={clearAll} style={{ ...btnStyle, color: 'red', fontSize: 12 }}>Limpiar</button>
            </div>
            <ul style={{ listStyle: 'none', padding: 0, margin: '4px 0', fontSize: 12 }}>
              {waypoints.map((wp, i) => {
                const isOrigin = wp.id === originId;
                const isDest = wp.id === destinationId;
                return (
                  <li key={wp.id} draggable
                    onDragStart={() => setDragIdx(i)} onDragOver={(e) => e.preventDefault()}
                    onDrop={() => { if (dragIdx !== null && dragIdx !== i) reorder(dragIdx, i); setDragIdx(null); }}
                    style={{ marginBottom: 3, padding: '4px 6px', cursor: 'grab', borderRadius: 4, background: dragIdx === i ? '#e0edff' : isOrigin ? '#e6f9e6' : isDest ? '#fde8e8' : '#f8f8f8', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      ☰ {isOrigin && '🟢 '}{isDest && '🔴 '}{wp.label}
                    </span>
                    <span style={{ whiteSpace: 'nowrap', display: 'flex', alignItems: 'center' }}>
                      <button onClick={() => toggleOrigin(wp.id)} style={{ ...roleBtnBase, background: isOrigin ? '#16a34a' : '#fff', color: isOrigin ? '#fff' : '#16a34a', borderColor: '#16a34a' }} title="Origen">O</button>
                      <button onClick={() => toggleDestination(wp.id)} style={{ ...roleBtnBase, background: isDest ? '#dc2626' : '#fff', color: isDest ? '#fff' : '#dc2626', borderColor: '#dc2626' }} title="Destino">D</button>
                      <span onClick={() => removePoint(wp.id)} style={{ color: 'red', cursor: 'pointer', marginLeft: 5 }}>✕</span>
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
        {info && (
          <div style={{ marginTop: 8, padding: '6px 8px', background: '#f0f7ff', borderRadius: 4, fontSize: 13, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span><b>{info.distance} km</b> — {info.duration} min</span>
            {movId && <button onClick={saveRoute} style={{ padding: '2px 8px', cursor: 'pointer', fontSize: 11, background: '#2563eb', color: '#fff', border: 'none', borderRadius: 3 }}>Guardar</button>}
          </div>
        )}
      </div>
      <div ref={mapContainerRef} style={{ width: '100%', height: '100%' }} />
    </div>
  );
};

export default RuteadorContent;
